import {Button as MUIButton, ButtonProps as MUIButtonProps} from '@mui/material';
import {darken, alpha} from '@mui/material/styles';
import {makeStyles} from '@mui/styles';
import {SNOW_BLIND, OUTSIDE_YELLOW, GRANITE_2, OUTSIDE_YELLOW_1, GRANITE_1} from '@gaia/colors';
import GaiaIcon, {GaiaIconProps} from '@gaia/icons/GaiaIcon';

const useStyles = makeStyles({
    root: {
        '&:focus': {
            outline: 'none',
        },
        '&.defaultFocused': {
            color: GRANITE_2,
            backgroundColor: OUTSIDE_YELLOW_1,
        },
        '&.primaryFocused': {
            backgroundColor: GRANITE_1,
        },
        '&.outlinedFocused': {
            color: GRANITE_2,
            borderColor: darken(OUTSIDE_YELLOW, 0.1),
            backgroundColor: OUTSIDE_YELLOW_1,
        },
    },
    default: {
        color: GRANITE_2,
        '&:hover': {
            color: GRANITE_2,
            backgroundColor: OUTSIDE_YELLOW_1,
        },
    },
    primary: {
        color: GRANITE_2,
        backgroundColor: OUTSIDE_YELLOW,
        '&:hover': {
            backgroundColor: GRANITE_1,
        },
    },
    secondary: {
        color: SNOW_BLIND,
        backgroundColor: GRANITE_1,
        '&:hover, &:focus, &.secondaryFocused': {
            backgroundColor: GRANITE_1,
            color: SNOW_BLIND,
            transform: 'scale(1.05)',
            transition: 'transform 175ms ease',
        },
    },
    outlineInverted: {
        color: OUTSIDE_YELLOW,
        borderColor: OUTSIDE_YELLOW,
        '&:hover': {
            color: darken(OUTSIDE_YELLOW, 0.1),
            borderColor: darken(OUTSIDE_YELLOW, 0.1),
            backgroundColor: alpha(OUTSIDE_YELLOW, 0.1),
        },
        '&.outlinedFocused': {
            color: darken(OUTSIDE_YELLOW, 0.1),
            borderColor: darken(OUTSIDE_YELLOW, 0.1),
            backgroundColor: alpha(OUTSIDE_YELLOW, 0.1),
        },
    },

    outlined: {
        color: GRANITE_2,
        borderColor: OUTSIDE_YELLOW,
        '&:hover': {
            color: GRANITE_2,
            borderColor: darken(OUTSIDE_YELLOW, 0.1),
            backgroundColor: OUTSIDE_YELLOW_1,
        },
    },
});

export interface OutsideButtonProps
    extends Omit<MUIButtonProps, 'disableRipple' | 'onFocusVisible' | 'buttonRef'> {
    /**
     * The visual appearance of the button. Overrides MUI `color` and `variant` props if provided.
     */
    appearance?: 'default' | 'primary' | 'secondary' | 'outlined';
    /**
     * Visually and functionally disables the button.
     */
    disabled?: boolean;
    /**
     * If `true`, the button will take up the full width of its container.
     */
    fullWidth?: boolean;
    /**
     * A URL. If provided, an `a` element will be rendered as the root node.
     */
    href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
    /**
     * Invert the color of an outlined button.
     */
    inverted?: boolean;
    /**
     * A custom icon to display at the left of the button. Uses naming conventions from `GaiaIcon`.
     */
    iconName?: GaiaIconProps['name'];
    /**
     * What happens when the button is clicked.
     */
    onClick?: (event: any) => void;
    /**
     * The size of the button. Defaults to `medium`.
     */
    size?: 'medium' | 'large' | 'small';
    /**
     * Optional styles.
     */
    style?: React.CSSProperties;
    /**
     * The content of the button.
     */
    children?: React.ReactNode;
    /**
     * If an href is provided, opens the URL in a new tab.
     */
    target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
    rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
}

// Map MUI style props to our button API
const mapMuiButtonProps: Record<string, MUIButtonProps> = {
    default: {
        variant: 'text',
        color: 'primary',
        focusVisibleClassName: 'defaultFocused',
    },
    primary: {
        variant: 'contained',
        color: 'primary',
        focusVisibleClassName: 'primaryFocused',
    },
    secondary: {
        variant: 'contained',
        color: 'secondary',
        focusVisibleClassName: 'secondaryFocused',
    },
    outlined: {
        variant: 'outlined',
        color: 'primary',
        focusVisibleClassName: 'outlinedFocused',
    },
};

/**
 * `<Button />` wraps the MUI Button component and inherits its props.
 */
export default function OutsideButton({
    appearance = 'default',
    disabled = false,
    size,
    inverted,
    iconName,
    onClick,
    ...props
}: OutsideButtonProps) {
    const classes = useStyles();
    const {style, children, ...buttonProps} = props;
    const buttonStyles = {
        root: classes.root,
        ...(appearance === 'default' && {textPrimary: classes.default}),
        ...(appearance === 'primary' && {containedPrimary: classes.primary}),
        ...(appearance === 'secondary' && {
            containedSecondary: classes.secondary,
        }),
        ...(appearance === 'outlined' && inverted
            ? {outlinedPrimary: classes.outlineInverted}
            : {outlinedPrimary: classes.outlined}),
    };
    return (
        <MUIButton
            {...buttonProps}
            {...mapMuiButtonProps[appearance]}
            disabled={disabled}
            onClick={onClick}
            classes={buttonStyles}
            size={size}
            style={style}
        >
            {iconName && <GaiaIcon name={iconName} style={{marginRight: 8, marginLeft: -4}} />}
            {children}
        </MUIButton>
    );
}
